import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessConfig, setAccessConfig] = useState({ allow_independent_signups: true });

  const checkAuth = useCallback(async () => {
    try {
      const me = await api.get("/auth/me", { timeout: 15000 });
      setUser(me.data);
    } catch {
      try {
        const { data } = await api.get("/access/config", { timeout: 10000 });
        setAccessConfig(data);
      } catch {
        setAccessConfig({ allow_independent_signups: true });
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/access/config", { timeout: 10000 })
      .then(({ data }) => setAccessConfig(data))
      .catch(() => setAccessConfig({ allow_independent_signups: true }));
  }, []);

  useEffect(() => {
    // If returning from OAuth callback (session_id in hash), skip /me — AuthCallback will handle it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginEmail = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password }, { timeout: 20000 });
    localStorage.setItem("ts_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const signupEmail = async (email, password, name, access = {}) => {
    const { data } = await api.post("/auth/signup", { email, password, name, ...access }, { timeout: 20000 });
    localStorage.setItem("ts_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("ts_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, accessConfig, loginEmail, signupEmail, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
